import React, { useEffect, useRef, useState } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { BidInputs, DEFAULT_BID_INPUTS, clampPct, compareToReference, computeMaxBid } from "../lib/maxBid";
import { formatCurrency } from "../lib/format";
import { capture } from "../lib/analytics";

// Max-bid calculator (#3209) on the property panel. Inputs are saved per
// property in this browser (localStorage), not on the account: a v1 that costs
// no schema, and the usage event says whether to carry it to the server.

interface Props {
  propertyId: string;
  /** The county's estimate, offered as the starting ARV. Never presented as a valuation. */
  suggestedArv: number | null;
  /** The figure to beat: the current bid, else the amount owed. */
  referenceAmount: number | null;
  referenceLabel: string;
}

const numOrNull = (s: string): number | null => {
  const t = s.replace(/[$,\s]/g, "");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const MaxBidCalculator: React.FC<Props> = ({ propertyId, suggestedArv, referenceAmount, referenceLabel }) => {
  const [saved, setSaved] = useLocalStorage<BidInputs>(`parcely.maxbid.${propertyId}`, DEFAULT_BID_INPUTS);
  const [open, setOpen] = useState(false);
  const reported = useRef(false);

  // The ARV in use: the user's, else the county estimate.
  const inputs: BidInputs = { ...saved, arv: saved.arv ?? suggestedArv };
  const result = computeMaxBid(inputs);
  const cmp = compareToReference(result.maxBid, referenceAmount);

  // One usage event per opened panel, when the figures first produce a bid.
  useEffect(() => {
    if (open && result.maxBid != null && !reported.current) {
      reported.current = true;
      capture("max_bid_calculated", { property_id: propertyId, used_county_estimate: saved.arv == null });
    }
  }, [open, result.maxBid, propertyId, saved.arv]);

  const set = (patch: Partial<BidInputs>) => setSaved({ ...saved, ...patch });
  const pct = (v: number) => String(Math.round(v * 1000) / 10);

  return (
    <section className="max-bid" aria-label="Max bid calculator">
      <button type="button" className="lv-chip max-bid-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        Max bid calculator {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="max-bid-body">
          <div className="max-bid-grid">
            <label>
              After-repair value (ARV)
              <input
                className="lv-chip bbx-num"
                inputMode="numeric"
                aria-label="After-repair value"
                placeholder={suggestedArv != null ? String(Math.round(suggestedArv)) : "$"}
                value={saved.arv ?? ""}
                onChange={(e) => set({ arv: numOrNull(e.target.value) })}
              />
            </label>
            <label>
              Repairs $
              <input
                className="lv-chip bbx-num"
                inputMode="numeric"
                aria-label="Repair budget"
                value={saved.repairs ?? ""}
                onChange={(e) => set({ repairs: numOrNull(e.target.value) })}
              />
            </label>
            <label>
              Holding costs $
              <input
                className="lv-chip bbx-num"
                inputMode="numeric"
                aria-label="Holding costs"
                value={saved.holding ?? ""}
                onChange={(e) => set({ holding: numOrNull(e.target.value) })}
              />
            </label>
            <label>
              Buy + sell costs % of ARV
              <input
                className="lv-chip bbx-num"
                inputMode="decimal"
                aria-label="Buying and selling costs percent"
                value={pct(saved.costsPct)}
                onChange={(e) => set({ costsPct: clampPct((numOrNull(e.target.value) ?? 0) / 100) })}
              />
            </label>
            <label>
              Target profit % of ARV
              <input
                className="lv-chip bbx-num"
                inputMode="decimal"
                aria-label="Target profit percent"
                value={pct(saved.marginPct)}
                onChange={(e) => set({ marginPct: clampPct((numOrNull(e.target.value) ?? 0) / 100) })}
              />
            </label>
          </div>

          <p className="max-bid-result" role="status">
            {result.maxBid == null ? (
              "Enter an after-repair value to get a max bid."
            ) : result.underwater ? (
              "At these numbers no bid clears your margin."
            ) : (
              <>
                Max bid <strong>{formatCurrency(result.maxBid)}</strong>
              </>
            )}
          </p>
          {cmp && (
            <p className={"max-bid-compare" + (cmp.overMax ? " max-bid-compare--over" : "")}>
              {cmp.overMax
                ? `${formatCurrency(Math.abs(cmp.room))} over your max, against ${referenceLabel}.`
                : `${formatCurrency(cmp.room)} of room under your max, against ${referenceLabel}.`}
            </p>
          )}
          <p className="max-bid-note">
            Your numbers, saved in this browser. Parcely has no sold comps for most of NC, so the ARV is yours to
            confirm{saved.arv == null && suggestedArv != null ? "; the county estimate is only a starting point" : ""}.
          </p>
        </div>
      )}
    </section>
  );
};

export default MaxBidCalculator;
